import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  getStageText,
  DistinctionOptions,
  GICSSubIndustries,
  RevenueModels,
  filterPlaceholderTagValues,
} from "@/lib/utils";

interface EligibilityCriteriaCardProps {
  // Industry tags (level 5 sub-industry codes)
  gicsIndustries?: string[];
  gicsSubIndustries?: string[];
  // Revenue models
  revenueModels?: string[];
  // Stage thesis — accepts legacy numeric entries for workspaces written
  // before the string-stage migration; getStageText canonicalises both.
  stages?: Array<string | number>;
  locations?: string[];
  businessModels?: string[];
  distinctions?: string[];
}

function getDistinctionText(value: string) {
  const found = DistinctionOptions.find((d) => d.value === value);
  return found ? found.text : value;
}

// Sub-component for searchable flat list sections
function SearchableTagSection({
  title,
  items,
  searchPlaceholder = "Search...",
}: {
  title: string;
  items: string[];
  searchPlaceholder?: string;
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const lower = searchTerm.toLowerCase();
    return items.filter((item) => item.toLowerCase().includes(lower));
  }, [items, searchTerm]);

  const showSearch = items.length > 5;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-2">{title}</p>
      {showSearch && (
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-7 pl-7 text-xs"
          />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {filtered.map((item) => (
          <span
            key={item}
            className="border border-gray-200 text-gray-700 text-sm px-3 py-1 rounded-md"
          >
            {item}
          </span>
        ))}
        {filtered.length === 0 && searchTerm && (
          <p className="text-xs text-gray-400">No matches found</p>
        )}
      </div>
    </div>
  );
}

export function EligibilityCriteriaCard({
  gicsIndustries = [],
  gicsSubIndustries = [],
  revenueModels,
  stages,
  locations,
  businessModels,
  distinctions,
}: EligibilityCriteriaCardProps) {
  // Resolve industry tag names from sub-industry codes
  const industryTagNames = useMemo(() => {
    return gicsSubIndustries
      .map((code) => GICSSubIndustries.find((si) => si.code === code)?.name || code)
      .sort();
  }, [gicsSubIndustries]);

  // Scrub placeholder sentinels (see filterPlaceholderTagValues) so
  // `["no information provided"]` is treated as empty and the section hides.
  const cleanRevenueModels = filterPlaceholderTagValues(revenueModels);
  const cleanLocations = filterPlaceholderTagValues(locations);
  const cleanBusinessModels = filterPlaceholderTagValues(businessModels);
  const cleanDistinctions = filterPlaceholderTagValues(distinctions);
  const cleanStages = (stages ?? []).filter(
    (s) =>
      typeof s === "number" ||
      (typeof s === "string" && s.trim() !== ""),
  );

  const hasIndustryTags = industryTagNames.length > 0 || gicsIndustries.length > 0;
  const hasRevenueModels = cleanRevenueModels.length > 0;
  const hasStages = cleanStages.length > 0;
  const hasLocations = cleanLocations.length > 0;
  const hasBusinessModels = cleanBusinessModels.length > 0;
  const hasDistinctions = cleanDistinctions.length > 0;

  // Hide card if ALL criteria are missing
  const hasCriteria =
    hasIndustryTags ||
    hasRevenueModels ||
    hasStages ||
    hasLocations ||
    hasBusinessModels ||
    hasDistinctions;

  if (!hasCriteria) return null;

  // Map revenue model values to labels
  const revenueModelLabels = cleanRevenueModels.map(
    (value) => RevenueModels.find((rm) => rm.value === value)?.label || value
  );

  // Map stage numbers to text
  const stageLabels = cleanStages.map((stage) => getStageText(stage));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-black mb-4">
        Eligibility Criteria
      </h3>
      <div className="space-y-4">
        {hasIndustryTags && (
          <SearchableTagSection
            title="Industry Tags"
            items={industryTagNames}
            searchPlaceholder="Search industry tags..."
          />
        )}

        {hasRevenueModels && revenueModelLabels && (
          <SearchableTagSection
            title="Revenue Model"
            items={revenueModelLabels}
            searchPlaceholder="Search revenue models..."
          />
        )}

        {hasStages && stageLabels && (
          <SearchableTagSection
            title="Company Stage"
            items={stageLabels}
            searchPlaceholder="Search stages..."
          />
        )}

        {hasLocations && (
          <SearchableTagSection
            title="Geographic Focus"
            items={cleanLocations}
            searchPlaceholder="Search locations..."
          />
        )}

        {hasBusinessModels && (
          <SearchableTagSection
            title="Business Model"
            items={cleanBusinessModels}
            searchPlaceholder="Search business models..."
          />
        )}

        {hasDistinctions &&
          (() => {
            const meaningfulDistinctions = cleanDistinctions.filter(
              (d) => d !== "Other" && d !== "Not Applicable"
            );
            if (meaningfulDistinctions.length === 0) return null;
            const distinctionLabels = meaningfulDistinctions.map((d) =>
              getDistinctionText(d)
            );
            return (
              <SearchableTagSection
                title="Founder Diversity"
                items={distinctionLabels}
                searchPlaceholder="Search distinctions..."
              />
            );
          })()}
      </div>
    </div>
  );
}
